import { forwardRef, memo, useState } from 'react';
import PropTypes from 'prop-types';

import { IconButton, InputAdornment } from '@mui/material';
import { ClockIcon, TimePicker } from '@mui/x-date-pickers-pro';

/**
 * Custom Material UI TimePicker component enhanced with additional styling and functionality.
 * Provides a text field with an icon to pick a time using a dialog-style clock interface.
 *
 * @param {Object} props - The properties passed to the TimePicker component.
 * @param {boolean} [props.error=false] - If true, shows an error state on the input element.
 * @param {boolean} [props.fullWidth=false] - If true, the input will take up the full width of its container.
 * @param {string} [props.helperText=''] - Optional helper text to display below the input.
 * @param {string} [props.size='medium'] - The size of the input element, can be 'small' or 'medium'.
 * @param {Object} [props.sx] - Custom Sx prop to pass styles directly into the input component.
 * @param {Date} [props.value=null] - The current time value of the picker, should be a Date object or null.
 * @param {Function} props.onChange - Function to call when the time changes. Receives the new time value as a parameter.
 * @param {Object} props.other - Other props that are passed directly to the MUI TimePicker component.
 */
const MTimePicker = forwardRef((props, ref) => {
  const {
    error = null,
    fullWidth = false,
    helperText = '',
    size = 'medium',
    sx,
    value = null,
    onChange,
    ...other
  } = props;

  const [open, setOpen] = useState(false);

  const handleChange = (newValue) => {
    onChange(newValue);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <TimePicker
      {...other}
      ampm={false}
      inputFormat="HH:mm"
      open={open}
      ref={ref}
      views={['hours', 'minutes']}
      value={value}
      onChange={handleChange}
      onClose={handleClose}
      slotProps={{
        textField: {
          className: 'time-picker',
          fullWidth,
          size,
          sx,
          error,
          helperText,
          InputProps: {
            endAdornment: (
              <InputAdornment position="end" onClick={handleOpen}>
                <IconButton edge="end">
                  <ClockIcon />
                </IconButton>
              </InputAdornment>
            )
          }
        }
      }}
    />
  );
});

MTimePicker.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'small']),
  sx: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func
};

export default memo(MTimePicker);
